<template>
  <div>
    <v-data-table :headers="headers" :items="orders" item-key="id" :mobile-breakpoint="0" hide-default-footer
      class="orders-table">
      <!-- รหัสออเดอร์ -->
      <template v-slot:[`item.orderNumber`]="{ item }">
        <span class="order-id-text">{{ item.orderNumber || item.id?.slice(-8).toUpperCase() || "-" }}</span>
      </template>

      <!-- ผู้ซื้อ -->
      <template v-slot:[`item.buyer`]="{ item }">
        <div class="buyer-name">{{ getBuyerName(item.buyer) }}</div>
        <div v-if="item.buyer?.email" class="buyer-email">{{ item.buyer.email }}</div>
      </template>

      <!-- รายการสินค้า -->
      <template v-slot:[`item.items`]="{ item }">
        <div v-for="(oi, idx) in (item.items || []).slice(0, 2)" :key="oi.id || idx" class="product-row">
          <span class="product-name">{{ oi.productName || "-" }}</span>
          <span class="product-qty">×{{ oi.quantity }}</span>
        </div>
        <div v-if="(item.items || []).length > 2" class="more-items">+{{ item.items.length - 2 }} รายการ</div>
      </template>

      <!-- ยอดรวม -->
      <template v-slot:[`item.grandTotal`]="{ item }">
        <span class="total-text">{{ formatPrice(item.grandTotal || item.totalPrice) }}</span>
      </template>

      <!-- ชำระเงิน -->
      <template v-slot:[`item.paymentStatus`]="{ item }">
        <v-chip x-small label :color="getPaymentStyle(item.paymentStatus).bg"
          :text-color="getPaymentStyle(item.paymentStatus).color" class="status-chip">
          <v-icon left size="12">{{ getPaymentStyle(item.paymentStatus).icon }}</v-icon>
          {{ getPaymentStyle(item.paymentStatus).label }}
        </v-chip>
      </template>

      <!-- จัดส่ง -->
      <template v-slot:[`item.shipStatus`]="{ item }">
        <v-chip x-small label :color="getShipStyle(item.shipStatus, item.status).bg"
          :text-color="getShipStyle(item.shipStatus, item.status).color" class="status-chip">
          <v-icon left size="12">{{ getShipStyle(item.shipStatus, item.status).icon }}</v-icon>
          {{ getShipStyle(item.shipStatus, item.status).label }}
        </v-chip>
      </template>

      <!-- วันที่ -->
      <template v-slot:[`item.createdAt`]="{ item }">
        <span class="date-text">{{ formatDate(item.createdAt) }}</span>
      </template>

      <!-- จัดการ -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="actions-cell">
          <!-- รายละเอียด -->
          <v-btn icon small @click="$emit('view', item)" class="action-icon-btn" title="รายละเอียด">
            <v-icon size="18" color="#64748b">mdi-eye-outline</v-icon>
          </v-btn>

          <!-- ยกเลิก -->
          <v-btn v-if="canCancel(item)" icon small class="action-icon-btn" title="ยกเลิกออเดอร์"
            @click="$emit('cancel', item)">
            <v-icon size="18" color="#ef4444">mdi-close-circle-outline</v-icon>
          </v-btn>

          <template v-if="item.paymentStatus === 'paid'">
            <!-- เรียกไรเดอร์ -->
            <v-btn v-if="canCallRider(item)" icon small class="action-icon-btn" title="เรียกไรเดอร์"
              @click="$emit('call-rider', item)">
              <v-icon size="18" color="#3b82f6">mdi-moped</v-icon>
            </v-btn>

            <!-- seeking_rider indicator + ยกเลิก -->
            <template v-else-if="item.status === 'seeking_rider'">
              <div class="seeking-badge">
                <v-progress-circular indeterminate size="12" width="2" color="#f59e0b" />
                <span>หาไรเดอร์...</span>
              </div>
              <v-btn icon small class="action-icon-btn ml-1" title="ยกเลิกการหาไรเดอร์"
                @click="$emit('cancel-seeking', item)">
                <v-icon size="16" color="#f59e0b">mdi-close</v-icon>
              </v-btn>
            </template>

            <!-- ดูไรเดอร์ในระยะ -->
            <v-btn
              v-if="item.ship?.isRiderFlow && !item.riderId && item.status !== 'cancelled' && item.shipStatus !== 'delivered'"
              icon small class="action-icon-btn" title="ดูไรเดอร์ในระยะ" @click="openNearbyDialog(item)">
              <v-icon size="18" color="#8b5cf6">mdi-map-marker-radius-outline</v-icon>
            </v-btn>

            <!-- ติดตามไรเดอร์ -->
            <v-btn
              v-if="item.riderId && item.ship?.isRiderFlow && ['processing', 'picked_up', 'delivering', 'rider_accepted'].includes(item.status)"
              icon small class="action-icon-btn" title="ติดตามไรเดอร์" @click="openTrackingDialog(item)">
              <v-icon size="18" color="#10b981">mdi-map-marker-path</v-icon>
            </v-btn>
          </template>

          <!-- บันทึกจัดส่ง (ขนส่งเอกชน) -->
          <v-btn v-if="canSaveDelivery(item)" icon small class="action-icon-btn" :title="item.trackingNumber ? 'ดูข้อมูลการจัดส่ง' : 'บันทึกการจัดส่ง'"
            @click="openDeliveryDialog(item)">
            <v-icon size="18" :color="item.trackingNumber ? '#3b82f6' : '#10b981'">
              {{ item.trackingNumber ? 'mdi-truck-delivery-outline' : 'mdi-truck-check-outline' }}
            </v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- ── Delivery Dialog ── -->
    <v-dialog v-model="deliveryDialog" max-width="560px">
      <v-card class="rounded-xl pa-1">
        <v-card-title class="pt-5 pb-2 px-6 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="#F4811F" class="mr-2">mdi-truck-delivery-outline</v-icon>
            <span class="text-subtitle-1 font-weight-black">บันทึกการจัดส่ง</span>
          </div>
          <v-btn icon small @click="closeDeliveryDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="px-6 pb-4">
          <!-- กรณีที่มีการบันทึกไปแล้ว ให้โชว์เป็นข้อมูลอย่างเดียว (แก้ไขไม่ได้) -->
          <div v-if="selectedOrder && selectedOrder.trackingNumber" class="tracking-box-display pa-5 rounded-xl">
            <div class="d-flex align-center justify-space-between mb-4">
              <div>
                <div class="tracking-label">เลขพัสดุ</div>
                <div class="tracking-num-text">{{ selectedOrder.trackingNumber }}</div>
              </div>
              <div class="tracking-icon-wrap">
                <v-icon color="#3b82f6" size="24">mdi-package-variant-closed</v-icon>
              </div>
            </div>
            <v-divider class="mb-4" />
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="tracking-label">บริษัทขนส่ง</div>
                <div class="tracking-company-text">{{ selectedOrder.vehicleType?.name || 'ไม่ระบุ' }}</div>
              </div>
              <v-btn v-if="getTrackingLink(selectedOrder)" :href="getTrackingLink(selectedOrder)" target="_blank"
                small color="#3b82f6" dark rounded elevation="0" class="px-4">
                <v-icon left size="14">mdi-open-in-new</v-icon>ติดตาม
              </v-btn>
            </div>
            <div v-if="selectedOrder.note" class="mt-4 pt-3 border-top-dash">
              <div class="tracking-label">หมายเหตุ</div>
              <div class="tracking-note-text">{{ selectedOrder.note }}</div>
            </div>
          </div>

          <!-- กรณีที่ยังไม่ได้บันทึก ให้โชว์ฟอร์มกรอก -->
          <template v-else>
            <div class="mb-4">
              <div class="field-label">บริษัทขนส่ง <span class="red--text">*</span></div>
              <div class="shipping-company-grid mb-2">
                <div v-for="company in filteredShippingCompanies" :key="company.id" class="company-selector-card"
                  :class="{ 'active': selectedVehicleType === company.id }" @click="selectedVehicleType = company.id">
                  <div class="active-indicator">
                    <v-icon size="12" color="white">mdi-check</v-icon>
                  </div>
                  <div class="company-name">{{ company.name }}</div>
                </div>
              </div>
            </div>
            <div class="mb-4">
              <div class="field-label">เลขพัสดุ <span class="red--text">*</span></div>
              <v-text-field v-model="trackingNumber" placeholder="กรอกเลขพัสดุ" outlined dense hide-details="auto"
                class="rounded-lg" background-color="#F9FAFB" />
            </div>
          </template>

          <!-- ส่วนของหมายเหตุ - ให้เพิ่มได้เรื่อยๆ ตลอดเวลา -->
          <div class="mt-4">
            <div class="field-label d-flex align-center">
              <span>หมายเหตุเพิ่มเติม</span>
              <v-chip x-small class="ml-2 px-2" color="#f1f5f9" text-color="#64748b" label>เพิ่มได้เรื่อยๆ</v-chip>
            </div>
            <v-textarea v-model="deliveryNote" placeholder="พิมพ์หมายเหตุที่นี่..." auto-grow outlined rows="2"
              hide-details="auto" class="rounded-lg" background-color="#F9FAFB" />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="closeDeliveryDialog" class="font-weight-bold">ยกเลิก</v-btn>
          <v-btn color="#F4811F" dark class="rounded-lg font-weight-bold px-6" elevation="0"
            :disabled="!isValidDelivery" :loading="isSaving" @click="onSaveDeliveryNote">
            <v-icon left small>mdi-content-save-outline</v-icon>
            {{ selectedOrder && selectedOrder.trackingNumber ? 'อัปเดตหมายเหตุ' : 'บันทึกการจัดส่ง' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Rider Tracking Dialog ── -->
    <v-dialog v-model="trackingDialog" max-width="560px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="pt-5 pb-2 px-6 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="#10b981" class="mr-2">mdi-map-marker-path</v-icon>
            <span class="text-subtitle-1 font-weight-black">ติดตามไรเดอร์</span>
          </div>
          <v-btn icon small @click="closeTrackingDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <rider-tracking-map v-if="trackingDialog && trackingOrder && trackingDestination"
            :rider-id="trackingOrder.riderId" :destination="trackingDestination"
            :destination-label="trackingDestinationLabel" height="360px" />
          <div v-else-if="trackingDialog && trackingOrder && !trackingDestination" class="pa-6 text-center grey--text">
            ไม่พบข้อมูลพิกัดปลายทาง
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text color="error" @click="closeTrackingDialog">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Nearby Riders Dialog ── -->
    <v-dialog v-model="nearbyDialog" max-width="520px">
      <v-card class="rounded-xl overflow-hidden">
        <div class="nearby-dialog-header d-flex align-center justify-space-between px-5 py-4">
          <div class="d-flex align-center" style="gap:10px">
            <div class="nearby-header-icon">
              <v-icon color="white" size="20">mdi-motorbike</v-icon>
            </div>
            <div>
              <div class="nearby-header-title">ไรเดอร์ในรัศมี 2 กม.</div>
              <div class="nearby-header-sub">อัปเดตทุก 10 วินาที</div>
            </div>
          </div>
          <v-btn icon small @click="closeNearbyDialog"><v-icon color="#64748b">mdi-close</v-icon></v-btn>
        </div>

        <!-- Map -->
        <div class="nearby-map-wrap">
          <div ref="nearbyMapEl" class="nearby-map-el"></div>
          <div class="map-interaction-blocker"></div>

          <!-- top label -->
          <div class="map-top-bar">
            <div class="map-pulse-dot"></div>
            <span>กำลังค้นหาในรัศมี 2 กม.</span>
          </div>

          <!-- rider count overlay -->
          <div class="nearby-map-badge">
            <span class="badge-count">{{ nearbyRiderCount }}</span>
            <span class="badge-label">คนขับในรัศมี</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="nearby-dialog-footer">
          <div class="d-flex align-center" style="gap:8px">
            <v-progress-circular v-if="nearbyLoading" indeterminate size="16" width="2" color="#F4811F" />
            <span class="nearby-status-text">{{ nearbyStatusText }}</span>
          </div>
          <v-btn small text color="error" @click="closeNearbyDialog">ปิด</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { updateOrderTrackingNumberApi } from "@/services/api/sellerServices";
import { getNearbyRiders } from "@/services/api/geography";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";
import swalAlert from "@/services/alert/swalServices";
import RiderTrackingMap from "@/components/Shared/RiderTrackingMap.vue";

/* global google */

export default {
  name: "OrderTable",

  components: { RiderTrackingMap },

  props: {
    orders: { type: Array, required: true },
    vehicleTypes: { type: Array, default: () => [] },
  },

  data() {
    return {
      // delivery dialog
      deliveryDialog: false,
      deliveryNote: "",
      trackingNumber: "",
      selectedVehicleType: null,
      selectedOrder: null,
      isSaving: false,

      // tracking dialog
      trackingDialog: false,
      trackingOrder: null,

      // nearby dialog
      nearbyDialog: false,
      nearbyMap: null,
      nearbyRiderMarkers: [],
      nearbyOriginMarker: null,
      nearbyRadiusCircle: null,
      nearbyRiderCount: 0,
      nearbyPollTimer: null,
      nearbyLoading: false,
      nearbyOrigin: null,
      nearbyOrderMeta: { vehicleTypeId: null, serviceTypeId: null },
    };
  },

  computed: {
    headers() {
      return [
        { text: "รหัสออเดอร์", value: "orderNumber", sortable: false, width: "130px" },
        { text: "ผู้ซื้อ", value: "buyer", sortable: false, width: "160px" },
        { text: "สินค้า", value: "items", sortable: false, width: "220px" },
        { text: "ยอดรวม", value: "grandTotal", sortable: false, width: "110px", align: "right" },
        { text: "ชำระเงิน", value: "paymentStatus", sortable: false, width: "150px" },
        { text: "การจัดส่ง", value: "shipStatus", sortable: false, width: "165px" },
        { text: "วันที่สั่ง", value: "createdAt", sortable: false, width: "130px" },
        { text: "จัดการ", value: "actions", sortable: false, width: "140px", align: "center" },
      ];
    },

    filteredShippingCompanies() {
      if (!this.selectedOrder?.shipId) return this.vehicleTypes;
      return this.vehicleTypes.filter((c) => !c.shipId || c.shipId === this.selectedOrder.shipId);
    },

    isValidDelivery() {
      // ถ้ามีเลขพัสดุแล้ว (โชว์แบบ Read-only) ให้กด Save ได้ตลอดเพื่ออัปเดต Note
      if (this.selectedOrder?.trackingNumber) return true;
      // ถ้ายังไม่มี ต้องกรอกทั้งบริษัทขนส่งและเลขพัสดุ
      return this.selectedVehicleType && (this.trackingNumber || "").trim().length > 0;
    },

    trackingDestination() {
      const o = this.trackingOrder;
      if (!o) return null;
      if (o.status === 'delivering') {
        const addr = o.address;
        if (addr?.lat && addr?.lng) return { lat: Number(addr.lat), lng: Number(addr.lng) };
      }
      const sellerAddr = o.seller?.address;
      if (sellerAddr?.lat && sellerAddr?.lng) return { lat: Number(sellerAddr.lat), lng: Number(sellerAddr.lng) };
      return null;
    },

    trackingDestinationLabel() {
      const o = this.trackingOrder;
      if (!o) return '';
      return o.status === 'delivering' ? 'ที่อยู่ผู้ซื้อ' : 'ร้านค้า';
    },

    nearbyStatusText() {
      if (this.nearbyLoading) return "กำลังโหลด...";
      if (this.nearbyRiderCount === 0) return "ไม่พบไรเดอร์ในรัศมี";
      return `พบ ${this.nearbyRiderCount} คนขับ`;
    },
  },

  methods: {
    // ── Rider call conditions ──
    canCancel(item) {
      return (
        item.status !== "cancelled" &&
        !["shipped", "out_for_delivery", "delivered"].includes(item.shipStatus) &&
        item.shipStatus !== "cancelled"
      );
    },

    canCallRider(item) {
      return (
        item.ship?.isRiderFlow &&
        !item.riderId &&
        item.status !== "seeking_rider" &&
        item.status !== "cancelled" &&
        item.shipStatus !== "delivered"
      );
    },

    canSaveDelivery(item) {
      return (
        !item.ship?.isRiderFlow &&
        item.paymentStatus === "paid" &&
        item.status !== "cancelled" &&
        item.shipStatus !== "delivered"
      );
    },

    // ── Delivery dialog ──
    openDeliveryDialog(item) {
      this.selectedOrder = item;
      this.deliveryNote = item.note || "";
      this.trackingNumber = item.trackingNumber || "";
      this.selectedVehicleType = item.vehicleTypeId || null;
      this.deliveryDialog = true;
    },

    closeDeliveryDialog() {
      this.deliveryDialog = false;
      this.deliveryNote = "";
      this.trackingNumber = "";
      this.selectedVehicleType = null;
      this.selectedOrder = null;
    },

    async onSaveDeliveryNote() {
      if (!this.selectedOrder || !this.isValidDelivery) return;
      try {
        this.isSaving = true;
        await updateOrderTrackingNumberApi(
          this.selectedOrder.id,
          this.trackingNumber.trim(),
          this.selectedVehicleType,
          this.deliveryNote
        );
        await swalAlert.Success("บันทึกการจัดส่งสำเร็จ", "อัปเดตข้อมูลการจัดส่งเรียบร้อยแล้ว");
        this.$emit("load-orders");
        this.closeDeliveryDialog();
      } catch (e) {
        swalAlert.Error("บันทึกไม่สำเร็จ", e?.response?.data?.message || "ไม่สามารถอัปเดตข้อมูลได้");
      } finally {
        this.isSaving = false;
      }
    },

    // ── Tracking dialog ──
    openTrackingDialog(item) {
      this.trackingOrder = item;
      this.trackingDialog = true;
    },

    closeTrackingDialog() {
      this.trackingDialog = false;
      this.trackingOrder = null;
    },

    // ── Nearby dialog ──
    openNearbyDialog(order) {
      this.nearbyOrderMeta = {
        vehicleTypeId: order?.vehicleTypeId || null,
        serviceTypeId: order?.serviceTypeId || null,
      };
      this.nearbyDialog = true;
      this.$nextTick(() => this.initNearbyMap());
    },

    closeNearbyDialog() {
      this.nearbyDialog = false;
      this.destroyNearbyMap();
    },

    async initNearbyMap() {
      this.nearbyLoading = true;
      try {
        const pos = await this.getCurrentPosition();
        this.nearbyOrigin = pos;
        if (!this.$refs.nearbyMapEl) return;

        await loadGoogleMapsScript();
        const center = { lat: pos.lat, lng: pos.lng };
        this.nearbyMap = new google.maps.Map(this.$refs.nearbyMapEl, {
          center,
          zoom: 13,
          disableDefaultUI: true,
          gestureHandling: "none",
          draggable: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          clickableIcons: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });

        this.nearbyRadiusCircle = new google.maps.Circle({
          map: this.nearbyMap,
          center,
          radius: 2000,
          fillColor: "#8b5cf6",
          fillOpacity: 0.08,
          strokeColor: "#8b5cf6",
          strokeWeight: 2,
          strokeOpacity: 0.6,
          clickable: false,
        });
        this.nearbyMap.fitBounds(this.nearbyRadiusCircle.getBounds(), 32);

        const originSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
          <filter id="ds"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#00000044"/></filter>
          <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26S32 26 32 16C32 8.268 25.732 2 18 2z" fill="#8b5cf6" filter="url(#ds)"/>
          <circle cx="18" cy="16" r="5" fill="white"/>
        </svg>`;
        this.nearbyOriginMarker = new google.maps.Marker({
          position: center,
          map: this.nearbyMap,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(originSvg),
            scaledSize: new google.maps.Size(36, 44),
            anchor: new google.maps.Point(18, 44),
          },
          title: "ร้านของคุณ",
        });

        await this.refreshNearbyRiders();
        this.nearbyPollTimer = setInterval(() => this.refreshNearbyRiders(), 10000);
      } catch (e) {
        console.warn("initNearbyMap:", e);
      } finally {
        this.nearbyLoading = false;
      }
    },

    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          (err) => reject(err),
          { timeout: 8000 }
        );
      });
    },

    async refreshNearbyRiders() {
      if (!this.nearbyOrigin || !this.nearbyMap) return;
      this.nearbyLoading = true;
      try {
        const { vehicleTypeId, serviceTypeId } = this.nearbyOrderMeta;
        const riders = await getNearbyRiders(
          this.nearbyOrigin.lat, this.nearbyOrigin.lng,
          undefined, serviceTypeId, vehicleTypeId
        );
        this.nearbyRiderCount = riders.length;
        this.nearbyRiderMarkers.forEach((m) => m.setMap(null));
        this.nearbyRiderMarkers = [];

        // Cluster riders at same location
        const zoom = this.nearbyMap.getZoom() || 14;
        const clusterRadiusDeg = 0.0012 * Math.pow(2, 14 - zoom);
        const clusters = [];
        riders.forEach((r) => {
          const lat = r.latitude ?? r.lat;
          const lng = r.longitude ?? r.lng;
          if (lat == null || lng == null) return;
          const existing = clusters.find(
            (c) => Math.abs(c.lat - lat) < clusterRadiusDeg && Math.abs(c.lng - lng) < clusterRadiusDeg
          );
          if (existing) {
            existing.count++;
            existing.lat = (existing.lat * (existing.count - 1) + lat) / existing.count;
            existing.lng = (existing.lng * (existing.count - 1) + lng) / existing.count;
          } else {
            clusters.push({ lat, lng, count: 1 });
          }
        });

        clusters.forEach((c) => {
          const isSingle = c.count === 1;
          const bg = isSingle ? "white" : "#F4811F";
          const stroke = isSingle ? "#F4811F" : "white";
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="54" height="64" viewBox="0 0 54 64">
            <circle cx="27" cy="27" r="25" fill="${bg}" stroke="${stroke}" stroke-width="2.5" opacity="0.95"/>
            <text y="36" font-size="26" text-anchor="middle" x="27">🛵</text>
            <rect x="6" y="46" width="42" height="16" rx="8" fill="${isSingle ? '#F4811F' : '#c75f00'}"/>
            <text y="59" font-size="12" font-weight="bold" fill="white" text-anchor="middle" x="27">${c.count} คัน</text>
          </svg>`;
          const m = new google.maps.Marker({
            position: { lat: c.lat, lng: c.lng },
            map: this.nearbyMap,
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
              scaledSize: new google.maps.Size(54, 64),
              anchor: new google.maps.Point(27, 32),
            },
            title: `${c.count} คัน`,
            zIndex: isSingle ? 1 : 10,
          });
          this.nearbyRiderMarkers.push(m);
        });
      } catch (e) {
        console.warn("refreshNearbyRiders:", e);
      } finally {
        this.nearbyLoading = false;
      }
    },

    destroyNearbyMap() {
      clearInterval(this.nearbyPollTimer);
      this.nearbyPollTimer = null;
      this.nearbyRiderMarkers.forEach((m) => m.setMap(null));
      this.nearbyRiderMarkers = [];
      if (this.nearbyOriginMarker) { this.nearbyOriginMarker.setMap(null); this.nearbyOriginMarker = null; }
      if (this.nearbyRadiusCircle) { this.nearbyRadiusCircle.setMap(null); this.nearbyRadiusCircle = null; }
      this.nearbyMap = null;
      this.nearbyRiderCount = 0;
      this.nearbyOrigin = null;
    },

    // ── Formatters ──
    getBuyerName(buyer) {
      if (!buyer) return "-";
      return (`${buyer.prefix || ""} ${buyer.firstName || ""} ${buyer.lastName || ""}`).trim() || buyer.username || "-";
    },

    formatPrice(price) {
      if (price == null) return "-";
      return `฿${Number(price).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },

    formatDate(d) {
      if (!d) return "-";
      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";
      const months = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear() + 543}`;
    },

    getPaymentStyle(status) {
      const s = (status || "").toLowerCase();
      if (s === "paid") return { label: "ชำระแล้ว", bg: "#f0fdf4", color: "#16a34a", icon: "mdi-check-circle-outline" };
      if (s === "pending") return { label: "รอชำระ", bg: "#eef2ff", color: "#6366f1", icon: "mdi-clock-outline" };
      if (s === "request_paid") return { label: "รอยืนยัน", bg: "#fffbeb", color: "#f59e0b", icon: "mdi-credit-card-clock" };
      if (s === "refunded") return { label: "คืนเงินแล้ว", bg: "#f5f3ff", color: "#7c3aed", icon: "mdi-cash-refund" };
      if (s === "cancelled") return { label: "ยกเลิก", bg: "#fef2f2", color: "#ef4444", icon: "mdi-close-circle-outline" };
      return { label: status || "-", bg: "#f1f5f9", color: "#64748b", icon: "mdi-help-circle-outline" };
    },

    getShipStyle(shipStatus, orderStatus) {
      const s = (shipStatus || "").toLowerCase();
      const os = (orderStatus || "").toLowerCase();
      if (s === "delivered") return { label: "จัดส่งสำเร็จ", bg: "#f0fdf4", color: "#16a34a", icon: "mdi-check-all" };
      if (s === "shipped") return { label: "ขนส่งเข้ารับแล้ว", bg: "#fff7ed", color: "#F4811F", icon: "mdi-truck-fast" };
      if (s === "picked_up") return { label: "ไรเดอร์รับแล้ว", bg: "#f0fdf4", color: "#10b981", icon: "mdi-package-variant-closed" };
      if (os === "seeking_rider") return { label: "กำลังหาไรเดอร์", bg: "#fffbeb", color: "#f59e0b", icon: "mdi-magnify" };
      if (os === "rider_accepted") return { label: "ไรเดอร์รับงาน", bg: "#eff6ff", color: "#3b82f6", icon: "mdi-moped" };
      if (s === "processing" || s === "preparing" || s === "packed") return { label: "เตรียมสินค้า", bg: "#eff6ff", color: "#3b82f6", icon: "mdi-package-variant" };
      if (s === "pending") return { label: "รอจัดส่ง", bg: "#eef2ff", color: "#6366f1", icon: "mdi-clock-outline" };
      if (s === "cancelled") return { label: "ยกเลิก", bg: "#fef2f2", color: "#ef4444", icon: "mdi-close-circle-outline" };
      return { label: shipStatus || "-", bg: "#f1f5f9", color: "#64748b", icon: "mdi-help-circle-outline" };
    },

    getTrackingLink(order) {
      if (!order?.trackingNumber || !order?.vehicleType?.link) return null;
      let url = order.vehicleType.link;
      url = url.replace("{trackingNumber}", order.trackingNumber);
      url = url.replace(":trackingNumber", order.trackingNumber);
      return url;
    },
  },
};
</script>

<style scoped>
/* ── Table ── */
.orders-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

::v-deep .orders-table thead th {
  background: #f8fafc !important;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  padding: 12px 14px !important;
}

::v-deep .orders-table tbody td {
  font-size: 13px !important;
  color: #1e293b !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  vertical-align: middle !important;
}

::v-deep .orders-table tbody tr:hover td {
  background: #fafbff !important;
}

/* ── Cell content ── */
.order-id-text {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  font-family: monospace;
}

.buyer-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.buyer-email {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}

.product-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.product-name {
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.product-qty {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
}

.more-items {
  font-size: 11px;
  color: #94a3b8;
}

.total-text {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.date-text {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.status-chip {
  font-size: 11px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  letter-spacing: 0 !important;
}

/* ── Actions cell ── */
.actions-cell {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
}

.action-icon-btn {
  border-radius: 8px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}

.action-icon-btn:hover {
  background: #f1f5f9 !important;
}

.seeking-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #f59e0b;
  white-space: nowrap;
  padding: 0 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

/* ── Nearby Dialog ── */
.nearby-dialog-header {
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
}

.nearby-header-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nearby-header-title {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
}

.nearby-header-sub {
  font-size: 11px;
  color: #94a3b8;
}

.nearby-map-wrap {
  position: relative;
  overflow: hidden;
}

.nearby-map-el {
  width: 100%;
  height: 320px;
  display: block;
}

.map-interaction-blocker {
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: default;
}

.map-top-bar {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(8px);
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
}

.map-pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
  animation: pulse-dot 1.6s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
  }

  70% {
    box-shadow: 0 0 0 7px rgba(74, 222, 128, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(74, 222, 128, 0);
  }
}

.nearby-map-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.75) 0%, transparent 100%);
  padding: 24px 18px 14px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  pointer-events: none;
  z-index: 3;
}

.badge-count {
  font-size: 30px;
  font-weight: 900;
  color: #fff;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.badge-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.nearby-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.nearby-status-text {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
/* ── Shipping Company Grid ── */
.shipping-company-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.company-selector-card {
  position: relative;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

.company-selector-card:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.company-selector-card.active {
  border-color: #F4811F;
  background: #fff7ed;
  box-shadow: 0 4px 12px rgba(244, 129, 31, 0.08);
}

.company-selector-card .company-name {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  line-height: 1.2;
}

.company-selector-card.active .company-name {
  color: #F4811F;
  font-weight: 700;
}

.active-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  background: #F4811F;
  border-radius: 50%;
  display: none;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.company-selector-card.active .active-indicator {
  display: flex;
}

@media (max-width: 600px) {
  .shipping-company-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ── Tracking Display Box ── */
.tracking-box-display {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.tracking-label {
  font-size: 11px;
  font-weight: 700;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.tracking-num-text {
  font-size: 20px;
  font-weight: 800;
  color: #0c4a6e;
  letter-spacing: 1px;
}

.tracking-company-text {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.tracking-note-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}

.tracking-icon-wrap {
  width: 44px;
  height: 44px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.1);
}

.border-top-dash {
  border-top: 1px dashed #cbd5e1;
}
</style>
